import ActionResult from 'app/src/engine/action-results/ActionResult';
import Instruction from 'app/src/engine/instructions/Instruction';
import MovableItem, { MovableItemLocation } from 'app/src/engine/movable-items/MovableItem';
import Robot from 'app/src/engine/movable-items/Robot';
import Star from 'app/src/engine/Star';
import TileInteraction from 'app/src/engine/tile-interactions/TileInteraction';
import Tile from 'app/src/engine/tiles/Tile';
import { cloneLevelDescription, LevelDescription } from 'app/src/LevelDescription';
import Logger from 'app/src/Logger';
import GamePlugin from 'app/src/plugins/GamePlugin';
import PubSub from 'app/src/utils/PubSub';

export enum GameStateError {
	MovableItemIdCollision,
	MovableItemFromLevelParserIsNotRobot,
	NoRobotsInLevelDescription,
	NoAvailableMovableItemProviderForMovableItemType,
}

export enum TileGridError {
	GridDescriptionHasUnevenRows,
	NoAvailableTileProviderForTileType,
	NoAvailableTileProviderForTileDescription,
}

export enum TurnPhase {
	GameStart,
	LoadInstructions,
	ExecuteInstructions,
	ExecuteTileInteractions,
}

export enum GameFinishedReason {
	AllStarsCollected,
	AllRobotsDead,
	NoRemainingInstructions,
	Reset,
}

export default class GameStateStore {
	// MARK: Pubsub
	private _onUpdate = new PubSub<void>();

	// MARK: Properties

	private _currentTurn = 0;
	private _currentTurnPhase = TurnPhase.GameStart;

	private _gamePlugins = new Set<GamePlugin>();

	private _tiles: Tile[][] = [];

	private _rows = 0;
	private _cols = 0;

	private _robots = new Set<Robot>();
	private _currentRobotExecution = 0;

	private _movableItems = new Set<MovableItem>();
	private _movableItemLocations = new Map<MovableItem, MovableItemLocation>();

	private _stars = new Set<Star>();
	private _collectedStars = 0;

	private _actionResults: ActionResult[][] = [];
	private _currentActionResultIndex: number | null = null;

	private _completedAnimations = 0;

	private _gameFinishedReason: GameFinishedReason | null = null;

	// Hold a reference to the level so we can reset later.
	private _levelDescription: LevelDescription;

	// Game plugins will register tile intractions (ex: conveyors moving movable
	// items). _tileInteractions is a list of these interactions ordered by priority.
	private _tileInteractions: TileInteraction[] = [];
	private _currentTileInteraction = 0;

	// Normally, when movable items are moved, GameState will cause a fatalError
	// when instructed to move one MovableItem on top of another, however, there
	// are situations where multiple robots will move in one action (ex: one robot
	// pushes another). In those cases, use start/finishMovementTransaction to
	// change this flag.
	private _allowMovableItemCollisions = false;

	// MARK: Getters

	get onUpdate(): PubSub<void> {
		return this._onUpdate;
	}

	get title(): string {
		return this._levelDescription.title;
	}

	get description(): string {
		return this._levelDescription.description;
	}

	get rows(): number {
		return this._rows;
	}

	get cols(): number {
		return this._cols;
	}

	get gamePlugins(): Set<GamePlugin> {
		return this._gamePlugins;
	}

	get currentTurn(): number {
		return this._currentTurn;
	}

	get currentTurnPhase(): TurnPhase {
		return this._currentTurnPhase;
	}

	get currentTileInteraction(): number {
		return this._currentTileInteraction;
	}

	get actionResults(): ActionResult[][] {
		return this._actionResults;
	}

	get currentActionResultIndex(): number | null {
		return this._currentActionResultIndex;
	}

	get currentActionResultSet(): ActionResult[] | null {
		if (this._currentActionResultIndex === null) {
			return null;
		}

		return this._actionResults[this._currentActionResultIndex] || null;
	}

	get robots(): Set<Robot> {
		return this._robots;
	}

	get currentRobotExecution(): number {
		return this._currentRobotExecution;
	}

	get movableItems(): Set<MovableItem> {
		return this._movableItems;
	}

	get movableItemLocations(): Map<MovableItem, MovableItemLocation> {
		return this._movableItemLocations;
	}

	get stars(): Set<Star> {
		return this._stars;
	}

	get collectedStars(): number {
		return this._collectedStars;
	}

	get gameFinishedReason(): GameFinishedReason | null {
		return this._gameFinishedReason;
	}

	get gameIsFinished(): boolean {
		return this._gameFinishedReason !== null;
	}

	public getTile(row: number, col: number): Tile {
		if (!(row < this._rows && row >= 0 && col < this._cols && col >= 0)) {
			throw new Error('row or col is out of range of this TileGrid.');
		}

		return this._tiles[row][col];
	}

	get tiles(): Tile[][] {
		return this._tiles;
	}

	constructor(levelDescription: LevelDescription) {
		// Hold a reference to the level so we can reset later.
		this._levelDescription = levelDescription;

		// Save the game plugins.
		this._gamePlugins = levelDescription.gamePlugins;

		// Cache the list of tile interactions.
		this._tileInteractions = [];

		// Add all plugins' TileInteractions to the list.
		for (const gamePlugin of levelDescription.gamePlugins) {
			this._tileInteractions = [...this._tileInteractions, ...gamePlugin.getTileInteractions()];
		}

		// Sort tile interaction plugins based on priority.
		this._tileInteractions.sort((first, second) => first.priority - second.priority);

		// Reset game board components.
		this.reset();
	}

	public reset(): void {
		// If we don't have a description yet, there's nothing to reset.
		if (!this._levelDescription) {
			return;
		}

		// Reset properties.
		this._currentTurn = 0;
		this._currentTurnPhase = TurnPhase.GameStart;
		this._gameFinishedReason = GameFinishedReason.Reset;
		this._collectedStars = 0;

		this._currentTileInteraction = 0;
		this._currentRobotExecution = 0;

		// Reset action results.
		this._actionResults = [];
		this._currentActionResultIndex = null;
		this._completedAnimations = 0;

		// Clone the level description.
		const clonedLevelDescription = cloneLevelDescription(this._levelDescription);

		this._tiles = clonedLevelDescription.tiles;
		this._stars = clonedLevelDescription.stars;
		this._robots = clonedLevelDescription.robots;
		this._movableItems = clonedLevelDescription.robots;
		this._movableItemLocations = new Map([
			...clonedLevelDescription.robotLocations,
			...clonedLevelDescription.movableItemLocations,
		]);

		// Now that the board has been filled with tiles, recalculate counts.
		this._rows = this._tiles.length;
		this._cols = this._tiles.length === 0 ? 0 : this._tiles[0].length;

		// Ensure there's at least one Robot to which we can issue instructions.
		if (!this._robots.size) {
			throw new Error('Level data must contain at least one robot.');
		}

		// Publish the update.
		this._onUpdate.publish();
	}

	public addMovableItem(movableItem: MovableItem, location: MovableItemLocation): void {
		// Integrity checks.
		for (const existingMovableItem of this._movableItems) {
			if (movableItem === existingMovableItem) {
				throw new Error('MovableItem is already in the game.');
			}

			if (movableItem.id === existingMovableItem.id) {
				throw GameStateError.MovableItemIdCollision;
			}
		}

		// Add the item.
		this._movableItems.add(movableItem);

		// Add the location.
		this._movableItemLocations.set(movableItem, location);

		// Publish the update.
		this._onUpdate.publish();
	}

	public removeMovableItem(movableItem: MovableItem): void {
		if (!this._movableItems.has(movableItem)) {
			throw new Error('MovableItem must exist on board.');
		}

		this._movableItems.delete(movableItem);
		this._movableItemLocations.delete(movableItem);

		if (movableItem instanceof Robot) {
			if (!this._robots.has(movableItem)) {
				throw new Error('Robot must exist on board.');
			}

			this._robots.delete(movableItem as Robot);
		}

		// Publish the update.
		this._onUpdate.publish();
	}

	public getMovableItem(row: number, col: number): MovableItem | null {
		this._movableItemLocations.forEach((location, item) => {
			if (location.row === row && location.col === col) {
				return item;
			}
		});

		return null;
	}

	public setInstructionForRobot(instruction: Instruction): void {
		if (this._robots.size === 0) {
			return;
		}

		const robot = this._robots.values().next().value!;
		robot.nextInstruction = instruction;

		// Publish the update.
		this._onUpdate.publish();
	}

	// MARK: Star Transformation Methods

	private _getStarForTile(row: number, col: number): Star | null {
		return (
			Array.from(this._stars).find((s) => s.location.row === row && s.location.col === col) || null
		);
	}

	private _collectStar(star: Star): void {
		if (!this._stars.has(star)) {
			throw new Error('Star must exist on board.');
		}

		star.collected = true;

		Logger.log(`Star collected at (${star.location.row}, ${star.location}).`);

		this._collectedStars += 1;
	}

	// MARK: Phase Management Methods

	public advanceToNextPhase(): void {
		if (this._gameFinishedReason === GameFinishedReason.Reset) {
			this._gameFinishedReason = null;
		}

		if (this._gameFinishedReason !== null) {
			throw new Error('Game must not be finished.');
		}

		switch (this._currentTurnPhase) {
			case TurnPhase.GameStart:
				// Move to next phase.
				this._currentTurnPhase = TurnPhase.LoadInstructions;
				break;

			case TurnPhase.LoadInstructions:
				// Move to next phase.
				this._currentTurnPhase = TurnPhase.ExecuteInstructions;
				this._currentRobotExecution = 0;
				break;

			case TurnPhase.ExecuteInstructions:
				// Move to next phase.
				this._currentTurnPhase = TurnPhase.ExecuteTileInteractions;
				this._currentTileInteraction = 0;
				break;

			case TurnPhase.ExecuteTileInteractions:
				// Move to next phase.
				this._currentTurnPhase = TurnPhase.LoadInstructions;
				this._currentTurn += 1;
				break;
		}

		// Publish the update.
		this._onUpdate.publish();
	}

	public completeAnimation(): void {
		if (this._currentActionResultIndex === null) {
			throw new Error('Cannot finish animation with no active animation results.');
		}

		this._completedAnimations += 1;

		const currentActionResultSet = this._actionResults[this._currentActionResultIndex];

		Logger.log(
			`Completed animation ${this._completedAnimations} of ${currentActionResultSet.length}.`,
		);

		if (this._completedAnimations === currentActionResultSet.length) {
			Logger.log(
				`All animations in set ${this._currentActionResultIndex} completed. Moving to next set.`,
			);

			this._completedAnimations = 0;
			this._currentActionResultIndex += 1;
		}

		// Publish the update.
		this._onUpdate.publish();
	}

	// MARK: Robot Program Execution Phase Methods

	public get numberOfRobotsWithPendingInstructions(): number {
		let remaining = 0;

		for (const robot of this._robots) {
			// Only count living robots.
			if (!robot.alive) {
				continue;
			}

			// Only count robots with pending instructions.
			if (robot.nextInstruction === null) {
				continue;
			}

			remaining += 1;
		}

		return remaining;
	}

	// Execute the next robot's instructions.
	public executeNextInstruction(): void {
		if (this._gameFinishedReason !== null) {
			throw new Error('Game must not be finished.');
		}

		const robotsWithInstructions: Robot[] = [];

		for (const robot of this._robots) {
			if (robot.alive && robot.nextInstruction !== null) {
				robotsWithInstructions.push(robot);
			}
		}

		robotsWithInstructions.sort((robotA, robotB) => {
			// If there's a tie for base priority, use instance priority.
			if (robotA.nextInstruction!.basePriority === robotB.nextInstruction!.basePriority) {
				return (
					(robotA.nextInstruction as Instruction).instancePriority -
					(robotB.nextInstruction as Instruction).instancePriority
				);
			}

			// Compare base priority.
			return (
				(robotA.nextInstruction as Instruction).basePriority -
				(robotB.nextInstruction as Instruction).basePriority
			);
		});

		if (!robotsWithInstructions.length) {
			throw new Error('There must be remaining robots with instructions to execute.');
		}

		this._executeInstruction(robotsWithInstructions[0]);

		// Publish the update.
		this._onUpdate.publish();
	}

	public finishGameForNoInstructions(): void {
		this._gameFinishedReason = GameFinishedReason.NoRemainingInstructions;

		Logger.log('Game finished. No more instructions.');

		// Publish the update.
		this._onUpdate.publish();
	}

	// Execute the given robot's next instruction and return true. If robot
	// is dead or has no instruction to execute, return false.
	private _executeInstruction(robot: Robot): void {
		if (!robot.alive) {
			throw new Error();
		}
		if (robot.nextInstruction === null) {
			throw new Error();
		}

		// Clean up after last phase.
		this._actionResults = [];
		this._currentActionResultIndex = null;

		// Give the tile an opportunity to transform the instruction.
		const robotLocation = this._movableItemLocations.get(robot) as MovableItemLocation;
		const transformedInstruction = this._tiles[robotLocation.row][
			robotLocation.col
		].transformInstruction(robot.nextInstruction!);

		// Execute the instruction piece by piece.
		const actionResults: ActionResult[][] = [];
		let gameFinishedReason: GameFinishedReason | null = null;

		while (!transformedInstruction.finishedExecuting) {
			// Execute a segment.
			actionResults.push(transformedInstruction.executeSegment(this, robot));

			// Check if a robot has collected a star.
			for (const currentRobot of this._robots) {
				const currentRobotLocation = this._movableItemLocations.get(
					currentRobot,
				) as MovableItemLocation;
				const star = this._getStarForTile(currentRobotLocation.row, currentRobotLocation.col);
				if (star && !star.collected) {
					actionResults.push(star.getActionResultsForCollection());

					this._collectStar(star);
				}
			}

			// Check if the game is over.
			if (!this._getLivingRobotsRemain()) {
				gameFinishedReason = GameFinishedReason.AllRobotsDead;
				break;
			}

			if (this._collectedStars === this._stars.size) {
				gameFinishedReason = GameFinishedReason.AllStarsCollected;
				break;
			}
		}

		// Notify delegate if action results were created.
		if (actionResults.length !== 0) {
			Logger.log('Adding action results to list while executing instructions.');

			this._actionResults = actionResults;
			this._currentActionResultIndex = 0;
		}

		// Notify delegate if game ended.
		if (gameFinishedReason !== null) {
			this._gameFinishedReason = gameFinishedReason;

			Logger.log('Game finished while executing instructions.');
		}

		// Clear the instruction from the robot.
		robot.nextInstruction = null;

		// Increment the robot execution count.
		this._currentRobotExecution += 1;
	}

	// MARK: Tile Interaction Phase Methods

	public get numberOfPendingTileInteractions(): number {
		return this._tileInteractions.length - this._currentTileInteraction;
	}

	public executeNextTileInteraction(): void {
		if (this._gameFinishedReason !== null) {
			throw new Error('Game must not be finished.');
		}

		if (this._currentTileInteraction >= this._tileInteractions.length) {
			throw new Error('No remaining tile interaction plugins to execute.');
		}

		// Clean up after last phase.
		this._actionResults = [];
		this._currentActionResultIndex = null;

		// Execute the plugin.
		const tileInteraction = this._tileInteractions[this._currentTileInteraction];

		const actionResults: ActionResult[][] = [];
		let gameFinishedReason: GameFinishedReason | null = null;

		while (!tileInteraction.finishedExecuting) {
			const tileInteractionActionResults = tileInteraction.executeSegment(this);

			if (tileInteractionActionResults.length) {
				actionResults.push(tileInteractionActionResults);
			}

			// Check if a robot has collected a star.
			for (const currentRobot of this._robots) {
				const currentRobotLocation = this._movableItemLocations.get(
					currentRobot,
				) as MovableItemLocation;
				const star = this._getStarForTile(currentRobotLocation.row, currentRobotLocation.col);
				if (star && !star.collected) {
					actionResults.push(star.getActionResultsForCollection());

					this._collectStar(star);
				}
			}

			// Check if the game is over.
			if (!this._getLivingRobotsRemain()) {
				gameFinishedReason = GameFinishedReason.AllRobotsDead;
				break;
			}

			if (this._collectedStars === this._stars.size) {
				gameFinishedReason = GameFinishedReason.AllStarsCollected;
				break;
			}
		}

		// Reset the plugin and prepare for next use.
		tileInteraction.reset();

		// Advance to the next plugin.
		this._currentTileInteraction += 1;

		// Notify delegate if action results were created.
		if (actionResults.length !== 0) {
			Logger.log('Adding action results to list while executing tile interactions.');

			this._actionResults = actionResults;
			this._currentActionResultIndex = 0;
		}

		// Notify delegate if game ended.
		if (gameFinishedReason !== null) {
			this._gameFinishedReason = gameFinishedReason;

			Logger.log('Game finished while executing tile interactions.');
		}

		// Publish the update.
		this._onUpdate.publish();
	}

	// MARK: MovableItem Transformation Methods

	public startMovementTransaction(): void {
		// Set a flag that allows collisions. We'll check for consistencies after
		// all movement has finished.
		this._allowMovableItemCollisions = true;

		// Publish the update.
		this._onUpdate.publish();
	}

	public moveItem(movableItem: MovableItem, location: MovableItemLocation): void {
		// Check if the item is going to collide with another.
		const tile = this._tiles[location.row][location.col];
		if (!this._allowMovableItemCollisions && !tile.allowsMultipleMovableItems) {
			for (const item of this._movableItems) {
				if (item === movableItem) {
					continue;
				}

				const itemLocation = this._movableItemLocations.get(item) as MovableItemLocation;
				if (itemLocation.row === location.row && itemLocation.col === location.col) {
					throw new Error(`MovableItem collision at {${location.row}, ${location.col}}`);
				}
			}
		}

		// Move the item.
		this._movableItemLocations.set(movableItem, location);

		// Publish the update.
		this._onUpdate.publish();
	}

	public finishMovementTransaction(): void {
		// Restore per-move consistency checks.
		this._allowMovableItemCollisions = false;

		// Make a grid so we can track movable item locations.
		const tileContainsMovableItem: boolean[][] = [];

		for (let row = 0; row < this._rows; row++) {
			const rowTiles: boolean[] = [];

			for (let col = 0; col < this._cols; col++) {
				rowTiles.push(false);
			}

			tileContainsMovableItem.push(rowTiles);
		}

		// Make sure no items are on top of each other.
		for (const item of this._movableItems) {
			const itemLocation = this._movableItemLocations.get(item) as MovableItemLocation;

			const tile = this._tiles[itemLocation.row][itemLocation.col];
			if (tile.allowsMultipleMovableItems) {
				continue;
			}

			if (tileContainsMovableItem[itemLocation.row][itemLocation.col]) {
				throw new Error(`MovableItem collision at {${itemLocation.row}, ${itemLocation.col}}`);
			}

			tileContainsMovableItem[itemLocation.row][itemLocation.col] = true;
		}

		// Publish the update.
		this._onUpdate.publish();
	}

	public killRobot(robot: Robot): void {
		if (!robot.alive) {
			throw new Error();
		}

		robot.alive = false;

		// Publish the update.
		this._onUpdate.publish();
	}

	private _getLivingRobotsRemain(): boolean {
		let livingRobotRemains = false;
		for (const robot of this._robots) {
			if (robot.alive) {
				livingRobotRemains = true;
			}
		}

		return livingRobotRemains;
	}
}
